import { IGroupedSelectOption, ISelectOption } from "lobbie";
import { createElement, useCallback } from "react";
import { Control, Controller, ControllerRenderProps } from "react-hook-form";
import Select, { MenuPosition } from "react-select";
import CreateableSelect from "react-select/creatable";
import {
    REACT_SELECT_GROUP_BADGE_STYLES,
    REACT_SELECT_GROUP_STYLES,
    REACT_SELECT_STYLING,
} from "src/constants/styling";
import { noop, toSelectOption } from "src/utils";
import LobbieSelectDropdownIndicator from "../select/LobbieSelectDropdownIndicator";

interface IProps {
    options: ISelectOption[] | IGroupedSelectOption[];
    group?: boolean;
    control: Control<any>;
    name: string;
    id?: string;
    isMulti?: boolean;
    isClearable?: boolean;
    isDisabled?: boolean;
    isSearchable?: boolean;
    placeholder?: string;
    menuPosition?: MenuPosition;
    value?: ISelectOption | ISelectOption[];
    defaultValue?: ISelectOption;
    autoComplete?: string;
    onChange?: (...event: any[]) => void;
    closeMenuOnSelect?: boolean;
    onCreateOption?: (inputValue: string) => void;
    classNamePrefix?: string;
}

const formatGroupLabel = (data: any) => {
    return (
        <div style={REACT_SELECT_GROUP_STYLES}>
            <span>{data.label}</span>
            <span style={REACT_SELECT_GROUP_BADGE_STYLES}>{data.options.length}</span>
        </div>
    );
};

const LobbieSelectForm: React.FC<IProps> = ({
    options,
    group,
    control,
    name,
    id,
    isMulti,
    isClearable,
    isDisabled,
    isSearchable,
    placeholder,
    menuPosition,
    value,
    defaultValue,
    autoComplete,
    onChange,
    closeMenuOnSelect,
    onCreateOption,
    classNamePrefix,
}) => {
    const renderSelect = useCallback(
        ({ field }: { field: ControllerRenderProps<any, any> }) => {
            const props = {
                ...field,
                inputId: id,
                isMulti,
                isClearable: typeof isClearable === "boolean" ? isClearable : true,
                isSearchable,
                isDisabled,
                options,
                placeholder,
                menuPosition,
                closeMenuOnSelect,
                components: {
                    DropdownIndicator: LobbieSelectDropdownIndicator,
                    IndicatorSeparator: noop,
                },
                styles: REACT_SELECT_STYLING,
                classNamePrefix: classNamePrefix || "cy-react-select",
            } as Record<string, any>;

            if (group) {
                props["formatGroupLabel"] = formatGroupLabel;
            }

            if (value || onChange) {
                props["value"] = value;
                props["onChange"] = onChange as (...event: any[]) => void;
            } else if (defaultValue) {
                props["defaultValue"] = defaultValue;
            }

            if (props.value?.id) {
                props["value"] = toSelectOption(
                    props.value.name || props.value.label,
                    props.value.id,
                );
            }

            if (onCreateOption) {
                props["onCreateOption"] = onCreateOption;
            }

            if (autoComplete) {
                props["autoComplete"] = autoComplete;
                props["aria-autocomplete"] = true;
            }

            return createElement(onCreateOption ? CreateableSelect : Select, props);
        },
        [
            id,
            options,
            group,
            isMulti,
            isClearable,
            isDisabled,
            isSearchable,
            placeholder,
            menuPosition,
            value,
            defaultValue,
            autoComplete,
            onChange,
            closeMenuOnSelect,
            onCreateOption,
            classNamePrefix,
        ],
    );

    return <Controller name={name} control={control} render={renderSelect} />;
};
export default LobbieSelectForm;
